import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  HostBinding,
  HostListener,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { I2gDialogService } from '../../services/dialog/i2g-dialog.service';
import { IDialogComponent, IDialogData } from '../dialog-templates/dialog.data';
import { IconSizesEnum, IIcon } from '../icon/icon.component';
import { ANIMATION_TIME } from './dialog-manager.data';
import { ITrackingObject } from 'i2g-ng-tracking';
import { I2gNgTrackingActionBuilder, ITrackingAction } from 'i2g-ng-tracking';

@Component({
  selector: 'i2g-dialog-manager',
  templateUrl: './dialog-manager.component.html',
  styleUrls: ['./dialog-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogManagerComponent implements OnInit {
  @ViewChild('dialogPlaceholder', { read: ViewContainerRef, static: false })
  dialogRef;

  private _isFullScreen = false;

  shouldRender: boolean; // used to render or delete the element
  shouldDisplay: boolean; // used for the fade in/out animation
  isActiveDialogDismissible: boolean;
  disableScrolling: boolean;

  // tracking
  trackingObject: ITrackingObject;
  trackingScreenName: string;
  closeButtonTrackingAction: ITrackingAction;

  closeIcon: IIcon;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private _dialogService: I2gDialogService,
    private _changeDetector: ChangeDetectorRef,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnInit() {
    this.shouldRender = false;

    this.closeIcon = {
      name: 'close-navigation',
      size: IconSizesEnum.medium,
    };

    this._dialogService.dialog$.subscribe((dialogData: IDialogData) => {
      if (!dialogData && !this.shouldRender) {
        return;
      }

      if (dialogData) {
        this._mountDialog(dialogData);
      } else {
        this._unmountDialog();
      }
    });
  }

  private _mountDialog(dialogData: IDialogData) {
    const { component, props } = dialogData;

    // checking for full screen behaviour
    this._isFullScreen = props.fullScreen || false;

    // 'isDismissible' from props takes precedence, but defaults to dialog component's static prop
    this.isActiveDialogDismissible =
      typeof props.isDismissible === 'boolean'
        ? props.isDismissible
        : component.isDismissible;

    // support for disabling scrolling
    this.disableScrolling =
      typeof props.disableScrolling === 'boolean'
        ? props.disableScrolling
        : false;

    // tracking
    this.trackingObject = props.trackingObject;
    this.trackingScreenName = props.trackingScreenName;
    if (this.trackingObject && this.trackingScreenName) {
      this.closeButtonTrackingAction = this._trackingActionBuilder.buildButtonTapped(
        'dismiss',
        this.trackingScreenName
      );
    }

    this.shouldRender = true;
    this._changeDetector.detectChanges();
    this.dialogRef.clear();

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
      component
    );
    const componentInstanceRef: IDialogComponent = this.dialogRef.createComponent(
      componentFactory
    ).instance;

    Object.assign(componentInstanceRef, props);

    setTimeout(() => {
      this.shouldDisplay = true;
      this._changeDetector.detectChanges();
    });
  }

  private _unmountDialog() {
    this.shouldDisplay = false;
    this._changeDetector.detectChanges();

    setTimeout(() => {
      this.dialogRef.clear();
      this.shouldRender = false;
      this._changeDetector.detectChanges();
    }, ANIMATION_TIME);
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(
    event: KeyboardEvent
  ) {
    this.closeCurrentDialog();
  }

  @HostBinding('class.full-screen')
  get isDialogFullscreen(): boolean {
    return this._isFullScreen;
  }

  onCloseClicked() {
    this.closeCurrentDialog();
  }

  onOverlayClicked(event: Event) {
    if ((event.target as Element).classList.contains('overlay')) {
      this.closeCurrentDialog();
    }
  }

  closeCurrentDialog() {
    if (this.isActiveDialogDismissible) {
      this._dialogService.closeDialog();
    }
  }
}
